import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import cx from 'classnames';
import style from './single-order.module.scss';
import {
  FaFileDownload as DownloadIcon,
  FaSyncAlt as RegenerateIcon,
  FaSpinner
} from 'react-icons/fa';
import { currencyFormat } from '../../../../utils/helpers/helpers';
import { format, parseISO, isValid as isValidDate } from 'date-fns';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../../components/Modals/ConfirmationModal/ConfirmationModal';
import { Order } from '../../../../models/Order/Order';
import {
  DocumentOrder,
  DocumentDownloadType,
  OrderStatus
} from '../../../../models/Order/contracts';

const OrderStatusClasses: { [key: string]: string } = {
  [OrderStatus.PENDING]: 'cs-pending-status',
  [OrderStatus.ACCEPTED]: 'cs-accepted-status',
  [OrderStatus.DECLINED]: 'cs-declined-status',
  [OrderStatus.CLIENT_ACCEPTED]: 'cs-accepted-status',
  [OrderStatus.CLIENT_DECLINED]: 'cs-declined-status',
  [OrderStatus.RENTED]: 'cs-rented-status',
  [OrderStatus.RETURNED]: 'cs-returned-status'
};

const StatusActions: {
  [key: string]: Array<{ status: OrderStatus; label: string }> | undefined;
} = {
  [OrderStatus.PENDING]: [
    { status: OrderStatus.ACCEPTED, label: 'Prihvati' },
    { status: OrderStatus.DECLINED, label: 'Odbij' }
  ],
  [OrderStatus.ACCEPTED]: [
    { status: OrderStatus.CLIENT_ACCEPTED, label: 'Prihvati u ime klijenta' }
  ],
  [OrderStatus.CLIENT_ACCEPTED]: [
    { status: OrderStatus.RENTED, label: 'Izdaj (Rent)' }
  ],
  [OrderStatus.CLIENT_DECLINED]: [],
  [OrderStatus.RENTED]: [{ status: OrderStatus.RETURNED, label: 'Vraćeno' }]
};

function SingleOrder() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [processingAction, setProcessingAction] = useState<OrderStatus | null>(
    null
  );
  const [showConfirm, setShowConfirm] = useState(false);
  const [nextStatusConfirm, setNextStatusConfirm] =
    useState<OrderStatus | null>(null);
  const [nextLabelConfirm, setNextLabelConfirm] = useState('');

  const { data: order, isLoading: isLoadingOrder } = useQuery<Order, Error>({
    queryKey: ['order', id],
    queryFn: () => Order.getOne(id!),
    enabled: !!id
  });

  const {
    data: documents,
    isLoading: isLoadingDocs,
    refetch: refetchDocs
  } = useQuery<DocumentOrder[], Error>({
    queryKey: ['order-documents', id],
    queryFn: () => order!.getDocuments(),
    enabled: !!order
  });

  const { mutate: updateOrderStatusMutate, isLoading: isUpdatingStatus } =
    useMutation<Order, Error, OrderStatus>(
      (newStatus) => Order.update(id!, { status: newStatus }),
      {
        onSuccess: (updatedOrderData) => {
          queryClient.setQueryData(['order', id], updatedOrderData);
          refetchDocs();
          setProcessingAction(null);
          toast.success('Status narudžbine je uspešno ažuriran!');
        },
        onError: (error) => {
          toast.error(`Greška pri ažuriranju statusa: ${error.message}`);
          setProcessingAction(null);
        }
      }
    );

  const { mutate: regenerateDocMutate, isLoading: isRegenerating } =
    useMutation<DocumentOrder, Error, number>(
      (id) => order!.regenerateDocument(id),
      {
        onSuccess: () => {
          toast.success('Dokument je uspešno regenerisan!');
          refetchDocs();
        },
        onError: (err) => {
          toast.error(`Neuspelo regenerisanje dokumenta: ${err.message}`);
        }
      }
    );

  // --- IMPROVEMENT: Use definitive values from the database ---
  const priceBreakdown = useMemo(() => {
    if (!order) {
      return {
        subtotal: 0,
        washingFeeValue: 0,
        taxValue: 0,
        grandTotal: 0
      };
    }

    // Convert all to numbers
    const grandTotal = Number(order.total_price ?? 0);
    const taxValue = Number(order.tax_value ?? 0);
    const washingFeeValue = Number(order.washing_fee_value ?? 0);
    const subtotal = grandTotal - taxValue - washingFeeValue;

    return { subtotal, washingFeeValue, taxValue, grandTotal };
  }, [order]);

  const formatDate = (dateInput: string | Date | undefined): string => {
    if (!dateInput) return 'N/A';
    const d = typeof dateInput === 'string' ? parseISO(dateInput) : dateInput;
    return isValidDate(d) ? format(d, 'dd.MM.yyyy') : 'Nevažeći datum';
  };

  const handleActionClick = (status: OrderStatus, label: string) => {
    setNextStatusConfirm(status);
    setNextLabelConfirm(label);
    setShowConfirm(true);
  };

  const confirmAction = () => {
    if (nextStatusConfirm) {
      setProcessingAction(nextStatusConfirm);
      updateOrderStatusMutate(nextStatusConfirm);
    }
    setShowConfirm(false);
    setNextStatusConfirm(null);
  };

  if (isLoadingOrder)
    return (
      <div className="cs-inner-container cs-loading-center">
        <p>Učitavanje detalja narudžbine...</p>
      </div>
    );
  if (!order)
    return (
      <div className="cs-inner-container cs-loading-center">
        <p>Narudžbina nije pronađena.</p>
      </div>
    );

  return (
    <div className="cs-inner-container">
      <h1 className="cs-title mb-2">Narudžbina broj {order.number_of_order}</h1>

      <div className={style.orderStatus}>
        <span className={style.text}>Status narudžbine:</span>
        <div
          className={cx(
            style.value,
            'cs-status-tab',
            OrderStatusClasses[order.status]
          )}
        >
          {order.status}
        </div>
      </div>

      <div className={style.singleOrderHeader}>
        <div className={style.info}>
          <h2 className={style.subtitle}>Informacije o porudzbini:</h2>
          <div className={style.item}>
            <div className={style.label}>Poručilac:</div>
            <div className={style.value}>{order.full_name}</div>
          </div>
          <div className={style.item}>
            <div className={style.label}>Email Adresa:</div>
            <div className={style.value}>{order.email}</div>
          </div>
          {order.type === 'company' && (
            <>
              <div className={style.item}>
                <div className={style.label}>Ime Kompanije:</div>
                <div className={style.value}>{order.company_name}</div>
              </div>
              <div className={style.item}>
                <div className={style.label}>Matični broj:</div>
                <div className={style.value}>{order.company_id_number}</div>
              </div>
              <div className={style.item}>
                <div className={style.label}>PIB:</div>
                <div className={style.value}>{order.company_tax_number}</div>
              </div>
            </>
          )}
          <div className={style.item}>
            <div className={style.label}>Početak Rezervacije:</div>
            <div className={style.value}>
              {formatDate(order.reservation_start)}
            </div>
          </div>
          <div className={style.item}>
            <div className={style.label}>Kraj Rezervacije:</div>
            <div className={style.value}>
              {formatDate(order.reservation_end)}
            </div>
          </div>
          {order.washing_fee && (
            <div className={style.item}>
              <div className={style.label}>Naknada za pranje:</div>
              <div className={style.value}>
                Da ({order.washing_fee_percentage}%)
              </div>
            </div>
          )}
          <div className={style.item}>
            <div className={style.label}>Faktor cene:</div>
            <div className={style.value}>{order.price_factor}x</div>
          </div>
          <div className={style.item}>
            <div className={style.label}>Dodatne napomene:</div>
            <div className={style.value}>{order.note || 'Nema napomene.'}</div>
          </div>
        </div>
        <div className={style.documentation}>
          {isLoadingDocs && (
            <div className="cs-loading">
              <FaSpinner className="animate-spin" /> Učitavanje...
            </div>
          )}
          {!isLoadingDocs && documents && documents.length > 0
            ? documents.map((doc) => (
                <div key={doc.id} className={style.documentItem}>
                  <p className={style.label}>
                    {doc.type === DocumentDownloadType.ACCEPTED_ORDER
                      ? 'Kalkulacija:'
                      : doc.type === DocumentDownloadType.DECLINED_ORDER
                      ? 'Odbijena porudžbina:'
                      : doc.type === DocumentDownloadType.RENTED_ORDER
                      ? 'Revers:'
                      : doc.type === DocumentDownloadType.RETURNED_ORDER
                      ? 'Povraćaj reversa:'
                      : doc.type}
                  </p>
                  <div className={style.documentButtonWrapper}>
                    <button
                      onClick={() => {
               
                        Order.downloadDocument(doc.id, doc.filename);
                      }}
                      title={`Preuzmi: ${doc.original_name}`}
                    >
                      <DownloadIcon />
                    </button>
                    <button
                      onClick={() =>
                        regenerateDocMutate(doc.id)
                      }
                      disabled={isRegenerating}
                      title="Regeneriši"
                    >
                      <RegenerateIcon />
                    </button>
                  </div>
                </div>
              ))
            : !isLoadingDocs && (
                <p className="cs-no-documents">Nema dostupnih dokumenata.</p>
              )}
        </div>
      </div>

      <div className={style.orderActions}>
        <ConfirmationModal
          show={showConfirm}
          title="Potvrda akcije"
          message={`Da li ste sigurni da želite: "${nextLabelConfirm}"?`}
          onConfirm={confirmAction}
          onCancel={() => setShowConfirm(false)}
        />
        {StatusActions[order.status]?.map(({ status: nextStatus, label }) => {
          const isCurrentlyProcessing = processingAction === nextStatus;
          const showSpinner =
            isCurrentlyProcessing && (isUpdatingStatus || isLoadingOrder);
          const isDisabled =
            isUpdatingStatus ||
            isLoadingOrder ||
            !StatusActions[order.status]?.some((a) => a.status === nextStatus);

          if (!label) return null; // Don't render a button if there's no label

          return (
            <button
              key={nextStatus}
              onClick={() => handleActionClick(nextStatus, label)}
              disabled={isDisabled}
              className={cx(
                'cs-status-button',
                style.statusButton,
                OrderStatusClasses[nextStatus] || 'cs-default-status'
              )}
            >
              {showSpinner ? <FaSpinner className="animate-spin" /> : label}
            </button>
          );
        })}
        {order.status === 'client-declined' && (
          <p className={style.infoText}>
            Ponuda je odbijena od strane klijenta.
          </p>
        )}
        {order.status === 'returned' && (
          <p className={style.infoText}>Porudzbina je uspešno završena.</p>
        )}
      </div>

      <h2 className="cs-subtitle mt-4">Sadržaj porudžbine</h2>
      <div className={style.orderList}>
        <div className={style.orderHeader}>
          <div className={style.itemImage}></div>
          <div className={style.itemName}>Naziv</div>
          <div className={style.itemId}>SKU</div>
          <div className={style.itemCategory}>Kategorija</div>
          <div className={style.itemStorage}>Skladište</div>
          <div className={style.itemPrice}>Cena [RSD]</div>
        </div>
        <div className={style.orderBody}>
          {order.products.map((product, index) => {
            const pivotData = order.productsPivot[index].pivot;
            const imageUrl = product.getMainImageUrl();
            return (
              <div key={`${product.id}-${index}`} className={style.orderItem}>
                <div className={style.itemImage}>
                  {imageUrl ? (
                    <img src={imageUrl} alt={product.name} />
                  ) : (
                    <span className={style.noImage}>No Img</span>
                  )}
                </div>
                <div data-label="Naziv:" className={style.itemName}>
                  {product.name}
                </div>
                <div data-label="SKU:" className={style.itemId}>
                  {product.sku}
                </div>
                <div data-label="Kategorija:" className={style.itemCategory}>
                  {product.category?.name ?? 'N/A'}
                </div>
                <div data-label="Skladište:" className={style.itemStorage}>
                  {product.storageStand
                    ? `${product.storageStand.storageRoom?.name || ''} / ${
                        product.storageStand.name
                      }`
                    : 'N/A'}
                </div>
                <div data-label="Cena:" className={style.itemPrice}>
                  {currencyFormat(pivotData.price * (order.price_factor ?? 1))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className={style.orderFooter}>
        <div className={style.totalsWrapper}>
          <div className={style.totalRow}>
            <span className={style.label}>Predračun (proizvodi x faktor):</span>
            <span className={style.value}>
              {currencyFormat(priceBreakdown.subtotal)}
            </span>
          </div>
          {order.washing_fee && (
            <div className={style.totalRow}>
              <span className={style.label}>
                Naknada za pranje ({order.washing_fee_percentage}%):
              </span>
              <span className={style.value}>
                {currencyFormat(Number(priceBreakdown.washingFeeValue))}
              </span>
            </div>
          )}
          <div className={style.totalRow}>
            <span className={style.label}>Ukupan PDV ({order.tax_rate}%):</span>
            <span className={style.value}>
              {currencyFormat(priceBreakdown.taxValue)}
            </span>
          </div>
          <div className={cx(style.totalRow, style.grandTotal)}>
            <span className={style.label}>Ukupno za plaćanje:</span>
            <span className={style.value}>
              {currencyFormat(priceBreakdown.grandTotal)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SingleOrder;
